import * as tslib_1 from "tslib";
import { Router } from "@angular/router";
// Third party packages
import moment from "moment";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, of, throwError } from "rxjs";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
// Custom packages
import { findRequestedUserRole } from "src/app/modules/auth/requested-user-role.utils";
import { DocumentArchivesService } from "src/app/modules/properties/document-archives.service";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { EnvironmentService } from "src/app/shared/services/environment.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { TankService } from "../tank/tank.service";
import * as i0 from "@angular/core";
import * as i1 from "../../shared/services/api.service";
import * as i2 from "@angular/router";
import * as i3 from "ngx-cookie-service";
import * as i4 from "../../shared/services/environment.service";
import * as i5 from "../../shared/services/config.service";
import * as i6 from "../../shared/services/helper.service";
import * as i7 from "../tank/tank.service";
import * as i8 from "../properties/document-archives.service";
var AuthService = /** @class */ (function () {
    /**
     * Class constructor
     */
    function AuthService(apiService, router, cookieService, environmentService, configService, helperService, tankService, documentArchivesService) {
        this.apiService = apiService;
        this.router = router;
        this.cookieService = cookieService;
        this.environmentService = environmentService;
        this.configService = configService;
        this.helperService = helperService;
        this.tankService = tankService;
        this.documentArchivesService = documentArchivesService;
        this.subscriptions = [];
        this.loggedUser$ = new BehaviorSubject(null);
        this.loggedUserRequestedRole$ = new BehaviorSubject("");
        this.loggedUserRequestedRoleType$ = new BehaviorSubject("");
        this.loggedUserRequestedRoleObj$ = new BehaviorSubject(null);
        this.loggedUserPermissions$ = new BehaviorSubject([]);
        this.sessionExpiresAt$ = new BehaviorSubject(0);
        this.isRoleChanging$ = new BehaviorSubject(false);
        this.tankDocumentsCount$ = new BehaviorSubject(0);
        this.historicalDocsCount$ = new BehaviorSubject(0);
    }
    /**
     * Log user in through apiService
     *
     * @since 1.0.0
     *
     * @param email User email
     * @param password User password
     */
    AuthService.prototype.login = function (email, password) {
        var _this = this;
        return this.apiService
            .apiCall("POST", "auth/login", { email: email, password: password }, undefined, undefined, undefined, "v2")
            .pipe(map(function (result) {
            var e_1, _a;
            if (result.status) {
                try {
                    _this.setSession(result);
                    var expiresAt = moment().add(result.expiresIn, "second").utc();
                    _this.loggedUser$.next(result.user);
                    _this.loggedUserRequestedRole$.next(result.user.userRoles[0]._id); // this.loggedUserRequestedRole$.next(result.user.roles[0]._id);
                    _this.loggedUserRequestedRoleType$.next(result.user.userRoles[0].role.type); // this.loggedUserRequestedRole$.next(result.user.roles[0]._id);
                    _this.loggedUserRequestedRoleObj$.next(result.user.userRoles[0]);
                    // Get tankDocumentsCount
                    _this.subscriptions.push(_this.tankService.getList().subscribe(function (tankRes) {
                        if (tankRes && tankRes.status) {
                            var tankData = tankRes.data || tankRes;
                            if (tankData && tankData.totalCount) {
                                _this.tankDocumentsCount$.next(tankData.totalCount);
                            }
                        }
                    }, function (tankErr) {
                        _this.helperService.handleError(tankErr);
                    }));
                    var userPermissions = [];
                    try {
                        for (var _b = tslib_1.__values(result.user.userRoles[0].role
                            .permissions), _c = _b.next(); !_c.done; _c = _b.next()) {
                            var permission = _c.value;
                            // for (const permission of result.user.roles[0].permissions) {
                            if (_this.manageGeneralPermission(result.user.userRoles[0].role.type, result.user.userRoles[0].generalPermission, permission.permissionId.key)) {
                                userPermissions.push(permission.permissionId.key);
                            }
                        }
                    }
                    catch (e_1_1) { e_1 = { error: e_1_1 }; }
                    finally {
                        try {
                            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                        }
                        finally { if (e_1) throw e_1.error; }
                    }
                    _this.checkLastUserDocs(userPermissions);
                    _this.loggedUserPermissions$.next(userPermissions);
                    _this.sessionExpiresAt$.next(expiresAt.valueOf());
                    return result;
                }
                catch (error) {
                    // Something went wrong with session setting, let's logout user
                    console.error("Error during session setting:\n", error);
                    _this.logout();
                    return {
                        status: false,
                    };
                }
            }
            console.error("Error during session setting");
            return {
                status: false,
            };
        }));
    };
    /**
     * Send an email with a reset-password token to the user
     *
     * @since 1.0.0
     *
     * @param email User email
     */
    AuthService.prototype.recoverPassword = function (email) {
        return this.apiService.apiCall("POST", "auth/recover-password", { email: email }, undefined, undefined, undefined, "v2");
    };
    /**
     * Check if current user is logged in
     *
     * @since 1.0.0
     *
     * @returns Observable<any>
     */
    AuthService.prototype.checkLogin = function () {
        var _this = this;
        return this.apiService
            .apiCall("GET", "auth/check", undefined, undefined, undefined, undefined, "v2")
            .pipe(tap(function (result) {
            var e_2, _a, e_3, _b;
            if (result.status) {
                var user = result.data;
                if (!user || !user.userRoles || !user.userRoles.length) {
                    console.error('checkLogin: invalid user data received');
                    return;
                }
                var expiresAt = moment().add(result.expiresIn, "second");
                _this.loggedUser$.next(user);
                if (_this.cookieService.get("requestedRole")) {
                    // The entry of the requested role IN the requested company, as the back-end picks
                    // it: the same role held in several companies matches the role id more than once
                    var reqRole = findRequestedUserRole(user.userRoles, _this.cookieService.get("requestedRole"), _this.cookieService.get("requestedCompany")); // const reqRole = user.userRoles.find(role => role._id === this.cookieService.get('requestedRole'));
                    if (!reqRole) {
                        console.error("Cannot find reqRole!");
                        return;
                    }
                    _this.loggedUserRequestedRole$.next(reqRole._id);
                    _this.loggedUserRequestedRoleType$.next(reqRole.role.type);
                    _this.loggedUserRequestedRoleObj$.next(reqRole);
                    var userPermissions = [];
                    try {
                        for (var _c = tslib_1.__values(reqRole.role.permissions), _d = _c.next(); !_d.done; _d = _c.next()) {
                            var permission = _d.value;
                            // for (const permission of reqRole.permissions) {
                            if (_this.manageGeneralPermission(reqRole.role.type, reqRole.generalPermission, permission.permissionId.key)) {
                                userPermissions.push(permission.permissionId.key);
                            }
                        }
                    }
                    catch (e_2_1) { e_2 = { error: e_2_1 }; }
                    finally {
                        try {
                            if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                        }
                        finally { if (e_2) throw e_2.error; }
                    }
                    _this.checkLastUserDocs(userPermissions);
                    _this.loggedUserPermissions$.next(userPermissions);
                }
                else {
                    _this.loggedUserRequestedRole$.next(user.userRoles[0]._id);
                    _this.loggedUserRequestedRoleType$.next(user.userRoles[0].role.type);
                    _this.loggedUserRequestedRoleObj$.next(user.userRoles[0]);
                    var userPermissions = [];
                    try {
                        for (var _e = tslib_1.__values(user.userRoles[0].role.permissions), _f = _e.next(); !_f.done; _f = _e.next()) {
                            var permission = _f.value;
                            if (_this.manageGeneralPermission(user.userRoles[0].role.type, user.userRoles[0].generalPermission, permission.permissionId.key)) {
                                userPermissions.push(permission.permissionId.key);
                            }
                        }
                    }
                    catch (e_3_1) { e_3 = { error: e_3_1 }; }
                    finally {
                        try {
                            if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                        }
                        finally { if (e_3) throw e_3.error; }
                    }
                    _this.checkLastUserDocs(userPermissions);
                    _this.loggedUserPermissions$.next(userPermissions);
                }
                _this.sessionExpiresAt$.next(expiresAt.valueOf());
                // Get tankDocumentsCount
                _this.subscriptions.push(_this.tankService.getList().subscribe(function (tankRes) {
                    if (tankRes && tankRes.status) {
                        var tankData = tankRes.data || tankRes;
                        if (tankData && tankData.totalCount) {
                            _this.tankDocumentsCount$.next(tankData.totalCount);
                        }
                    }
                }, function (tankErr) {
                    _this.helperService.handleError(tankErr);
                }));
            }
        }));
    };
    /**
     * Log user out
     *
     * @since 1.0.0
     *
     * @param redirect True if after logout there should be a redirect
     * to login page. Default false.
     * @returns boolean true if user was logged out, false otherwhise
     */
    AuthService.prototype.logout = function (redirect) {
        var withNavigate = redirect ? redirect : false;
        // this.cookieService.delete('s.id', '/', this.environmentService.getFrontEndHost());
        // this.cookieService.delete('s.expiresAt', '/', this.environmentService.getFrontEndHost());
        // this.cookieService.delete('s.refreshToken', '/', this.environmentService.getFrontEndHost());
        // this.cookieService.delete('s.refreshTokenExpiresAt', '/', this.environmentService.getFrontEndHost());
        // this.cookieService.delete('requestedRole', '/', this.environmentService.getFrontEndHost());
        // this.cookieService.delete('requestedCompany', '/', this.environmentService.getFrontEndHost());
        // Delete cookies
        this.cookieService.set("s.id", "", new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        this.cookieService.set("s.expiresAt", "", new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        this.cookieService.set("s.refreshToken", "", new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        this.cookieService.set("s.refreshTokenExpiresAt", "", new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        this.cookieService.set("requestedRole", "", new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        this.cookieService.set("requestedCompany", "", new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        this.loggedUser$.next(null);
        this.loggedUserRequestedRole$.next("");
        this.loggedUserRequestedRoleType$.next("");
        this.loggedUserRequestedRoleObj$.next({});
        this.loggedUserPermissions$.next([]);
        this.sessionExpiresAt$.next(0);
        this.tankDocumentsCount$.next(0);
        this.historicalDocsCount$.next(0);
        if (withNavigate) {
            var lang = this.cookieService.get("lang") || this.configService.defaultLang;
            this.router.navigate(["/", lang, "auth", "login"]);
        }
        return true;
    };
    /**
     * Refresh current token (s.id) using refresh token
     *
     * @since 1.0.0
     *
     * @returns Observable<any>
     */
    AuthService.prototype.refreshToken = function (redirectIfLogout) {
        var _this = this;
        if (redirectIfLogout === void 0) { redirectIfLogout = true; }
        var refreshTokenExp = +this.cookieService.get("s.refreshTokenExpiresAt");
        var refreshToken = this.cookieService.get("s.refreshToken");
        if (typeof this.cookieService.get("s.refreshTokenExpiresAt") === undefined ||
            (typeof this.cookieService.get("refreshToken") === undefined &&
                moment.unix(moment.now()) > moment.unix(refreshTokenExp))) {
            this.logout(redirectIfLogout);
            return of(false);
        }
        if (!this.loggedUser$.value || !this.loggedUser$.value._id) {
            this.logout(redirectIfLogout);
            return;
        }
        // The raw token: ApiService adds the "Bearer " prefix itself, and the back-end refused the doubled
        // one (it read "Bearer" as the token)
        return this.apiService
            .apiCall("POST", "auth/refreshtoken", {}, { "X-Refresh-Token": refreshToken }, undefined, undefined, "v2")
            .pipe(switchMap(function (result) {
            // ApiService emits a network failure (status 0) as an Error value instead of throwing it:
            // the session may still be valid, so the renewal fails without logging out
            if (result instanceof Error) {
                return throwError({ status: 0, error: null });
            }
            _this.setSession(result.data);
            var expiresAt = moment().add(result.data.expiresIn, "second");
            _this.sessionExpiresAt$.next(expiresAt.valueOf());
            return of(true);
        }), catchError(function (err) {
            if (err && err.status === 0) {
                return throwError(err);
            }
            _this.logout(redirectIfLogout);
            return of(false);
        }));
    };
    /**
     * Set given role as the default requested role
     *
     * @since 1.0.0
     */
    AuthService.prototype.setRequestRoleId = function (id, roleId) {
        var e_4, _a;
        var _this = this;
        // Set role id on request header
        this.cookieService.set("requestedRole", roleId, null, "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        // Set role id on BehaviorSubject
        this.loggedUserRequestedRole$.next(id);
        // Set permissions
        var loggedUserPermissions = [];
        if (this.loggedUser$.getValue()) {
            var requestedRole = this.loggedUser$
                .getValue()
                .userRoles.find(function (obj) {
                return obj._id === id;
            });
            if (requestedRole) {
                // Set company reference in request header if company is present...
                if (requestedRole.company) {
                    this.cookieService.set("requestedCompany", requestedRole.company._id.toString(), null, "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
                }
                else {
                    // ...otherwise remove it from req header
                    if (this.cookieService.get("requestedCompany")) {
                        // this.cookieService.delete(
                        //   'requestedCompany',
                        //   '/',
                        //   this.environmentService.getFrontEndHost(),
                        // );
                        // Delete cookie
                        this.cookieService.set("requestedCompany", "", new Date("Thu, 01 Jan 1970 00:00:01 GMT"), "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
                    }
                }
                // Set role type
                if (requestedRole.role && requestedRole.role.type) {
                    this.loggedUserRequestedRoleType$.next(requestedRole.role.type);
                    this.loggedUserRequestedRoleObj$.next(requestedRole);
                }
                try {
                    // Set user's permissions
                    for (var _b = tslib_1.__values(requestedRole.role.permissions), _c = _b.next(); !_c.done; _c = _b.next()) {
                        var permission = _c.value;
                        if (permission.permissionId && permission.permissionId.key) {
                            if (this.manageGeneralPermission(requestedRole.role.type, requestedRole.generalPermission, permission.permissionId.key)) {
                                loggedUserPermissions.push(permission.permissionId.key);
                            }
                        }
                    }
                }
                catch (e_4_1) { e_4 = { error: e_4_1 }; }
                finally {
                    try {
                        if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                    }
                    finally { if (e_4) throw e_4.error; }
                }
                this.checkLastUserDocs(loggedUserPermissions);
                this.loggedUserPermissions$.next(loggedUserPermissions);
            }
        }
        else {
            // If we came here means loggedUser$ has not been emitted (yet!)
            // Se we must subscribe to that behaviourSubject and wait...
            this.loggedUser$.pipe(take(1)).subscribe(function (res) {
                var e_5, _a;
                if (res) {
                    var requestedRole = res.userRoles.find(function (obj) { return obj._id === id; }); // const requestedRole = res.roles.find(role => role._id === id);
                    if (!requestedRole) {
                        return;
                    }
                    if (requestedRole.company) {
                        // Get companyId and set it as req header
                        _this.cookieService.set("requestedCompany", requestedRole.company._id.toString(), null, "/", _this.environmentService.getFrontEndHost(), _this.environmentService.getFrontEndCookieSecure(), "Strict");
                    }
                    try {
                        // Set user's permissions
                        for (var _b = tslib_1.__values(requestedRole.role.permissions), _c = _b.next(); !_c.done; _c = _b.next()) {
                            var permission = _c.value;
                            if (permission.permissionId && permission.permissionId.key) {
                                if (_this.manageGeneralPermission(requestedRole.role.type, requestedRole.generalPermission, permission.permissionId.key)) {
                                    loggedUserPermissions.push(permission.permissionId.key);
                                }
                            }
                        }
                    }
                    catch (e_5_1) { e_5 = { error: e_5_1 }; }
                    finally {
                        try {
                            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
                        }
                        finally { if (e_5) throw e_5.error; }
                    }
                    _this.checkLastUserDocs(loggedUserPermissions);
                    _this.loggedUserPermissions$.next(loggedUserPermissions);
                }
            });
        }
    };
    /**
     * Emit new data for current logged user
     * This is often used once user update his profile to other
     * components can know changes in real time
     *
     * @since 1.0.0
     */
    AuthService.prototype.updatedLoggedUserData = function () {
        this.checkLogin().subscribe();
    };
    /**
     * Set given login result data as current session
     *
     * @since 1.0.0
     *
     * @param data Session data coming from back-end
     */
    AuthService.prototype.setSession = function (data) {
        var expiresAt = moment().add(data.expiresIn, "second");
        this.cookieService.set("s.id", data.token, null, "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        this.cookieService.set("s.expiresAt", JSON.stringify(expiresAt.valueOf()), null, "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        // Only the login answer carries the user: the renewal of the token (`{ token, expiresIn }`) must
        // not reset the role the user is acting with to the first one
        if (data.user && data.user.userRoles && data.user.userRoles[0]) {
            this.cookieService.set("requestedRole", data.user.userRoles[0].role._id, 
            // data.user.roles[0]._id,
            null, "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
            if (data.user.userRoles[0].company) {
                this.cookieService.set("requestedCompany", data.user.userRoles[0].company._id.toString(), null, "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
            }
        }
        if (data.refreshToken) {
            this.cookieService.set("s.refreshToken", data.refreshToken, null, "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        }
        if (data.refreshTokenExpiresIn) {
            var refreshExpiresAt = moment().add(data.refreshExpiresIn, "second");
            this.cookieService.set("s.refreshTokenExpiresAt", JSON.stringify(refreshExpiresAt.valueOf()), null, "/", this.environmentService.getFrontEndHost(), this.environmentService.getFrontEndCookieSecure(), "Strict");
        }
    };
    /**
     * Manage General Permission of Admin, Operator, Master and Collaborator user
     *
     * @since 1.0.0
     */
    AuthService.prototype.manageGeneralPermission = function (roleType, generalPermission, permissionKey) {
        // `null` is read as every switch off, as the back-end reads it (`hasEffectivePerms`): read as
        // it is, the first `generalPermission.<switch>` below would throw and break the session
        if (generalPermission === null) {
            return this.manageGeneralPermission(roleType, {}, permissionKey);
        }
        if (typeof generalPermission === "undefined" && roleType !== "temporary") {
            return true;
        }
        // Amministrazione
        // Accedere e modificare i dati amministrativi e fiscali (date abbonamento, fatturazioni, ecc.)
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.administration === "undefined" ||
            !generalPermission.administration) {
            if (permissionKey === "canReadPricesList" ||
                permissionKey === "canCreatePrices" ||
                permissionKey === "canDeletePrices" ||
                permissionKey === "canReadBalancesList" ||
                permissionKey === "canCreateBalances" ||
                permissionKey === "canExportBalances" ||
                permissionKey === "canReadSubscriptionsList" ||
                permissionKey === "canExportSubscriptions" ||
                permissionKey === "canDeleteSubscriptions" ||
                permissionKey === "canCreateSubscriptions") {
                return false;
            }
        }
        // Azienda
        // Accesso a società per visualizzazione e modifica dati società per Admin e Operator
        // Accesso a società per visualizzazione per Master e Collaborator
        if ((roleType === "admin" || roleType === "operator") &&
            (typeof generalPermission === "undefined" ||
                typeof generalPermission.editCompanies === "undefined" ||
                !generalPermission.editCompanies)) {
            if (permissionKey === "canDeleteCompanies" ||
                permissionKey === "canCreateCompanies" ||
                permissionKey === "canReadCompaniesList" ||
                permissionKey === "canExportCompanies" ||
                permissionKey === "canViewCompany" ||
                permissionKey === "canUpdateCompanies") {
                return false;
            }
        }
        if ((roleType === "master" || roleType === "collaborator") &&
            (typeof generalPermission === "undefined" ||
                typeof generalPermission.companies === "undefined" ||
                !generalPermission.companies)) {
            if (permissionKey === "canDeleteCompanies" ||
                permissionKey === "canCreateCompanies" ||
                permissionKey === "canReadCompaniesList" ||
                permissionKey === "canExportCompanies" ||
                permissionKey === "canViewCompany" ||
                permissionKey === "canUpdateCompanies") {
                return false;
            }
        }
        // Immobili
        // Accesso immobili per visualizzazione mappatura, rimappatura,
        // visualizzazione database documenti ed esportazione in excel dell’elenco dei documenti inseriti e mancanti
        if (roleType !== "temporary") {
            if (typeof generalPermission === "undefined" ||
                typeof generalPermission.properties === "undefined" ||
                !generalPermission.properties) {
                if (permissionKey === "canExportProperties" ||
                    permissionKey === "canDeleteProperties" ||
                    permissionKey === "canCreateProperties" ||
                    permissionKey === "canReadPropertiesList" ||
                    permissionKey === "canEditProperties" ||
                    permissionKey === "canCreateCondoStructures" ||
                    permissionKey === "canReadCondoStructuresList" ||
                    permissionKey === "canCreateDocuments" ||
                    permissionKey === "canReadDocumentsList" ||
                    permissionKey === "canDeleteDocuments" ||
                    permissionKey === "canSearchDocuments" ||
                    permissionKey === "canCreatePaperDocuments" ||
                    permissionKey === "canCreateDigitalDocuments" ||
                    permissionKey === "canDeleteAgreements" ||
                    permissionKey === "canEditDocumentsData") {
                    return false;
                }
            }
        }
        // Immobili
        // Elenco delle prossime scadenze degli immobili
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.timetable === "undefined" ||
            !generalPermission.timetable) {
            if (permissionKey === "canExportPropertiesExpirations" ||
                permissionKey === "canReadPropertiesExpirationsList") {
                return false;
            }
        }
        // Immobili
        // Ricerca documenti
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.docSearch === "undefined" ||
            !generalPermission.docSearch) {
            if (permissionKey === "canSearchDocuments") {
                return false;
            }
        }
        // Immobili
        // Inserimento documenti cartacei
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.insertPaperDoc === "undefined" ||
            !generalPermission.insertPaperDoc) {
            if (permissionKey === "canCreatePaperDocuments") {
                return false;
            }
        }
        // Immobili
        // Inserimento documenti digitali
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.insertDigitalDoc === "undefined" ||
            !generalPermission.insertDigitalDoc) {
            if (permissionKey === "canCreateDigitalDocuments") {
                return false;
            }
        }
        // Immobili
        // Possibilità di eliminare un documento inserito (cestino)
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.delDoc === "undefined" ||
            !generalPermission.delDoc) {
            if (permissionKey === "canDeleteDocuments") {
                return false;
            }
        }
        // Immobili
        // Possibilità di eliminare un contratto inserito (cestino)
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.delAgreement === "undefined" ||
            !generalPermission.delAgreement) {
            if (permissionKey === "canDeleteAgreements") {
                return false;
            }
        }
        // Immobili
        // Possibilità di modificare i campi aggiuntivi dei documenti, tranne quelli con scadenze e i contratti a rinnovo automatico (pennina)
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.editDocData === "undefined" ||
            !generalPermission.editDocData) {
            if (permissionKey === "canEditDocumentsData") {
                return false;
            }
        }
        // Utenti
        // Creare, eliminare e modificare i permessi di accesso dei Collaboratori
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.users === "undefined" ||
            !generalPermission.users) {
            if (permissionKey === "canDeleteUsers" ||
                permissionKey === "canReadUsersList" ||
                permissionKey === "canCreateUsers" ||
                permissionKey === "canExportUsers" ||
                permissionKey === "canChangeUsersStatus" ||
                permissionKey === "canCreateCondoUsers" ||
                permissionKey === "canCreateCondoAdmin") {
                return false;
            }
        }
        // Utenti temporanei
        // Creare, modificare ed eliminare gli accessi temporanei
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.temporaries === "undefined" ||
            !generalPermission.temporaries) {
            if (permissionKey === "canDeleteTemporaryUsers" ||
                permissionKey === "canReadTemporaryUsersList" ||
                permissionKey === "canCreateTemporaryUsers" ||
                permissionKey === "canExportTemporaryUsers" ||
                permissionKey === "canChangeTemporaryUsersStatus") {
                return false;
            }
        }
        // Invio email da piattaforma
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.email === "undefined" ||
            !generalPermission.email) {
            if (permissionKey === "canReadCommunicationsList" ||
                permissionKey === "canCreateCommunications" ||
                permissionKey === "canDeleteCommunications") {
                return false;
            }
        }
        // View Alert of the latest documents inserted
        if (typeof generalPermission === "undefined" ||
            typeof generalPermission.alertLastDocs === "undefined" ||
            !generalPermission.alertLastDocs) {
            if (permissionKey === "canViewLatestDocsInsList") {
                return false;
            }
        }
        return true;
    };
    /**
     * Verify that there are the latest documents inserted by the previous check
     *
     * @since 1.0.0
     */
    AuthService.prototype.checkLastUserDocs = function (loggedUserPermissions) {
        var _this = this;
        if (loggedUserPermissions.indexOf("canViewLatestDocsInsList") >= 0) {
            this.subscriptions.push(this.documentArchivesService.checkHistoricalDocs().subscribe(function (res) {
                if (res.status) {
                    _this.historicalDocsCount$.next(res.data.totalCount);
                }
            }, function () {
                // Only an alert: a refused or failed count shows none, with no error in the console
                _this.historicalDocsCount$.next(0);
            }));
        }
        else {
            this.historicalDocsCount$.next(0);
        }
    };
    AuthService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function AuthService_Factory() { return new AuthService(i0.ɵɵinject(i1.ApiService), i0.ɵɵinject(i2.Router), i0.ɵɵinject(i3.CookieService), i0.ɵɵinject(i4.EnvironmentService), i0.ɵɵinject(i5.ConfigService), i0.ɵɵinject(i6.HelperService), i0.ɵɵinject(i7.TankService), i0.ɵɵinject(i8.DocumentArchivesService)); }, token: AuthService, providedIn: "root" });
    return AuthService;
}());
export { AuthService };
